import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { getRouteApi, Link, useNavigate } from '@tanstack/react-router';
import { LAB_GATE_LABELS, type ImagingWorklistRow, type ImagingWorklistState } from '@wodi/shared';
import {
  Button,
  Cell,
  Column,
  EmptyState,
  LoadingBlock,
  Notice,
  PageHeader,
  Row,
  Table,
  TableBody,
  TableHeader,
  Tag,
  useShortcut,
} from '@wodi/ui';

import { imagingWorklistQuery } from '../api/imaging.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import { screen, ShortcutHint, TableScroll } from '../layout/screen.tsx';
import { formatInstant } from '../patients/format.ts';
import { heldBySha, ShaApprovalTag } from '../sha/approval-tag.tsx';
import { errorText } from '../stock/common.tsx';
import { formatWaiting, modalityWords, WORKLIST_TABS } from './format.ts';
import own from './imaging.module.css';

// The imaging worklist (docs/plans/phase2-radiology.md §6), modelled on the lab's: five
// lists in the order work flows, a dense table per modality, urgent first then the longest
// wait. Addresses carry the list and internal IDs only.

const route = getRouteApi('/_app/imaging');

const EMPTY: Record<ImagingWorklistState, string> = {
  requested: 'Nothing to schedule. New imaging requests appear here once they are placed.',
  scheduled: 'Nothing scheduled.',
  performed: 'No exams waiting for a report.',
  reported: 'No reports waiting for verification.',
  verified: 'No reports verified today yet.',
};

const TIME_COLUMN: Record<ImagingWorklistState, string> = {
  requested: 'Requested',
  scheduled: 'Scheduled for',
  performed: 'Performed',
  reported: 'Reported',
  verified: 'Verified',
};

function timeOf(row: ImagingWorklistRow, state: ImagingWorklistState): string | null {
  switch (state) {
    case 'requested':
      return row.orderedAt;
    case 'scheduled':
      return row.scheduledFor;
    case 'performed':
      return row.performedAt;
    case 'reported':
      return row.reportedAt;
    case 'verified':
      return row.verifiedAt;
  }
}

export function ImagingWorklistPage() {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const { state } = route.useSearch();
  const navigate = useNavigate();
  const tab = state ?? 'requested';
  const goTo = (next: ImagingWorklistState) => () => {
    void navigate({ to: '/imaging', search: { state: next } });
  };
  useShortcut('1', goTo('requested'), { description: 'Requested' });
  useShortcut('2', goTo('scheduled'), { description: 'Scheduled' });
  useShortcut('3', goTo('performed'), { description: 'Performed' });
  useShortcut('4', goTo('reported'), { description: 'Reported' });
  useShortcut('5', goTo('verified'), { description: 'Verified today' });

  if (session === null || !can(session, 'imaging.read')) {
    return (
      <>
        <PageHeader title="Imaging" />
        <Notice tone="info">
          You don't have permission to see the imaging worklist. Ask an administrator if you need
          it.
        </Notice>
      </>
    );
  }
  return (
    <div className={own.page}>
      <PageHeader title="Imaging" />
      <nav aria-label="Imaging worklist" className={screen.tabs}>
        {WORKLIST_TABS.map((t) => (
          <Link
            key={t.state}
            to="/imaging"
            search={{ state: t.state }}
            aria-keyshortcuts={t.key}
            {...(t.state === tab ? { 'aria-current': 'page' as const } : {})}
          >
            {t.label}
          </Link>
        ))}
      </nav>
      <ShortcutHint combo="1" to="5" action="switch between these lists" />
      <Worklist state={tab} />
    </div>
  );
}

function byModality(rows: readonly ImagingWorklistRow[]) {
  const groups = new Map<string, { name: string; rows: ImagingWorklistRow[] }>();
  for (const row of rows) {
    const key = row.modality ?? 'unset';
    const group = groups.get(key) ?? { name: modalityWords(row.modality), rows: [] };
    group.rows.push(row);
    groups.set(key, group);
  }
  return [...groups.entries()];
}

function Worklist({ state }: { state: ImagingWorklistState }) {
  const online = useOnline();
  const list = useQuery(imagingWorklistQuery(state));
  if (list.isPending) return <LoadingBlock label="Loading the worklist" rows={5} />;
  if (list.isError)
    return (
      <Notice
        tone="error"
        action={
          <Button dense onPress={() => void list.refetch()}>
            Try again
          </Button>
        }
      >
        {online
          ? errorText(list.error, "The worklist couldn't be loaded. Try again.")
          : 'No connection to Wodi. The worklist loads once the connection is back.'}
      </Notice>
    );
  if (list.data.items.length === 0) return <EmptyState>{EMPTY[state]}</EmptyState>;
  const waitingList = state === 'requested' || state === 'scheduled';
  return (
    <>
      {byModality(list.data.items).map(([key, group]) => (
        <section
          key={key}
          className={`${own.section} ${screen.panel}`}
          aria-labelledby={`m-${key}`}
        >
          <h2 id={`m-${key}`} className={own.sectionHeading}>
            {group.name}
          </h2>
          <TableScroll>
            <Table flat dense aria-labelledby={`m-${key}`}>
              <TableHeader>
                <Column isRowHeader>Patient</Column>
                <Column>Exam</Column>
                <Column>Priority</Column>
                <Column>{TIME_COLUMN[state]}</Column>
                <Column numeric>{waitingList ? 'Waiting' : 'Since'}</Column>
                <Column>Status</Column>
              </TableHeader>
              <TableBody>
                {group.rows.map((row) => {
                  const at = timeOf(row, state);
                  return (
                    <Row key={row.orderId} id={row.orderId}>
                      <Cell>
                        <span className={own.stack}>
                          <span>{row.patient.displayName}</span>
                          <span className={`wodi-id ${own.meta}`}>{row.patient.patientNumber}</span>
                        </span>
                      </Cell>
                      <Cell>
                        <Link to="/imaging/exams/$orderId" params={{ orderId: row.orderId }}>
                          {row.itemName}
                        </Link>
                      </Cell>
                      <Cell>
                        {row.priority === 'urgent' ? <Tag state="active">Urgent</Tag> : 'Routine'}
                      </Cell>
                      <Cell>{at === null ? '' : formatInstant(at)}</Cell>
                      <Cell numeric>
                        <span className={own.waiting}>{formatWaiting(row.minutesWaiting)}</span>
                      </Cell>
                      <Cell>
                        {waitingList ? (
                          <span className={own.actions}>
                            {heldBySha(row.shaApproval) ? null : (
                              <span>
                                {row.gate === 'ready' ? 'Ready' : LAB_GATE_LABELS[row.gate]}
                              </span>
                            )}
                            <ShaApprovalTag approval={row.shaApproval} />
                          </span>
                        ) : state === 'reported' ? (
                          <Tag state="attention">Not verified</Tag>
                        ) : state === 'verified' ? (
                          <Tag state="done">Verified</Tag>
                        ) : (
                          <Tag state="attention">Not reported</Tag>
                        )}
                      </Cell>
                    </Row>
                  );
                })}
              </TableBody>
            </Table>
          </TableScroll>
        </section>
      ))}
    </>
  );
}
